import { Component, OnInit, Input, input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, FormControl } from '@angular/forms';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { ModalController } from '@ionic/angular';
import { MEAL_PLANS } from '../../shared/constants/api-constants';
import { InputValidationsService } from '../../shared/services/validations/input-validations.service';
import { InventoryHotelsService } from 'src/app/business-service/inventory/inventory-hotels.service';
import { ComponentReloadService } from '../../shared/services/reload/component-reload.service';
import { GlobalService } from '../../shared/services/global/global.service';

@Component({
  selector: 'app-add-hotel',
  templateUrl: './add-hotel.component.html',
  styleUrls: ['./add-hotel.component.scss'],
})
export class AddHotelComponent implements OnInit {
  @Input() hotelId: any;
  @Input() hotelData: any;
  @Input() editHotelId: any;
  hotelPricingForm!: FormGroup;
  accommodationType: any;
  MEAL_PLAN = MEAL_PLANS;
  roomType: any = [];
  roomCategory: any = [];
  meals: any = [];
  todayDate: string = new Date().toISOString().split('T')[0]; // Restrict startDate to today or past
  startDateValue: string = ""; // Store updated minEndDate dynamically


  // Hardcoded facility options (can be replaced with API data)
  facilitiesOptions: any[] = [
    { name: 'Free Wifi', key: 'Free Wifi' },
    { name: 'Bar/Lounge', key: 'Bar/Lounge' },
    { name: 'Gym/Fitness', key: 'Gym/Fitness' },
    { name: 'Swimming pool', key: 'Swimming pool' },
    { name: 'Live Music', key: 'Live Music' },
  ];

  constructor(private _fb: FormBuilder,
    private _lovDataService: LovDataService,
    private _modalController: ModalController,
    private _validationService: InputValidationsService,
    private _hotelsService: InventoryHotelsService,
    private _notificationServices: ResponseNotificationService,
    private _reloadService: ComponentReloadService,
    private _globalService: GlobalService
  ) { }

  ngOnInit() {
    this.hotelPricingForm = this._fb.group({
      hotelId: [null],
      hotelDetails: this._fb.group({
        location: ['', Validators.required],
        hotelName: ['', Validators.required],
        hotelCategory: ['', Validators.required],
        hotelWebsite: ['', this._validationService.validateWebsiteUrl],
        availableNights: [0, Validators.required],
        address1: ['', Validators.required],
        address2: [''],
        groupedAddress: [null],
        firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern('^[a-zA-Z ]*$')]],
        lastName: ['', [Validators.minLength(2), Validators.maxLength(50), Validators.pattern('^[a-zA-Z ]*$')]],
        phoneNumber: [{ countryCode: '+91', phone: '' }],
        contactEmail: ['', [
          Validators.required,
          Validators.email,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
          Validators.maxLength(254)
        ]]
      }),
      currency: ['USD', [Validators.required]],
      status: ['Active'],
      roomDetailsAndPricing: this._fb.array([this.createRoomAndPricingFormGroup()]),
      notes: [''],
      id: [null],
      ownershipRole: [''],
      organizationId: [null]
    });

    this._lovDataService.getLovData("HotelType").subscribe((data) => {
      if (data) {
        this.accommodationType = data;
      }
    });
    this._lovDataService.getLovData("RoomCategory").subscribe((data) => {
      if (data) {
        this.roomCategory = data;

      }
    });
    this._lovDataService.getLovData("RoomType").subscribe((data) => {
      if (data) {
        this.roomType = data;
      }
    });
    this._lovDataService.getLovData("Meals").subscribe((data) => {
      this.meals = data;
    });

    this.hotelPricingForm.valueChanges.subscribe(() => {
      this.hotelPricingForm.markAllAsTouched();
    })

    // If hotelData exists, patch the form
    if (this.editHotelId) {
      this.getHotelDetails();
    }
    // dates validations
    this.hotelPricingForm.get('roomDetailsAndPricing')?.valueChanges.subscribe(() => {
      this.updateEndDateMinValues();
    });
  }

  // Function to update minEndDate
  updateEndDateMinValues() {
    this.hotelPricingForm.get('roomDetailsAndPricing')?.value?.forEach((pricing: any, pricingIndex: number) => {
      pricing.occupancyPricings.forEach((occupancy: any, occupancyIndex: number) => {
        const startDateControl = this.hotelPricingForm.get(
          `roomDetailsAndPricing.${pricingIndex}.occupancyPricings.${occupancyIndex}.priceValidity.startDate`
        );
        if (startDateControl?.value) {
          let minEndDate = new Date(startDateControl.value);
          minEndDate.setDate(minEndDate.getDate() + 1); // Ensure endDate is at least 1 day after
          this.startDateValue = minEndDate.toISOString().split('T')[0]; // Store for HTML binding
        }
      });
    });
  }

  // room Details and Pricing Section form Group
  createRoomAndPricingFormGroup(): FormGroup {
    let group = this._fb.group({
      roomType: ['', [Validators.required]],
      nearByPlace: [''],
      mealPlans: [[]],
      facilities: new FormControl([]),
      occupancyPricings: this._fb.array([this.createPaxFormgroup()])
    });
    return group;
  }

  // Create Pax FormGroup
  createPaxFormgroup(): FormGroup {
    return this._fb.group({
      paxRange: this._fb.group({
        minRange: [null],
        maxRange: [null],
      }),
      occupancy: this._fb.group({
        sgl: [null],
        dbl: [null],
        tpl: [null]
      }),
      childCost: this._fb.group({
        withBed: [null],
        withoutBed: [null]
      }),
      priceValidity: this._fb.group({
        startDate: [''],
        endDate: ['']
      })
    });
  }

  // atleast One pricing to be filled
  atLeastOneOccupancyRequired(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control || !(control instanceof FormGroup)) {
        return null;
      }

      const sgl = control.get('sgl')?.value;
      const dbl = control.get('dbl')?.value;
      const tpl = control.get('tpl')?.value;

      if (sgl || dbl || tpl) {
        return null; // At least one value exists, so no error
      }

      return { atLeastOneRequired: true }; // Error when all are null or empty
    };
  }

  // Get room details and Pricing Section
  getRoomAndPricingFormArray(): FormArray {
    return this.hotelPricingForm.get('roomDetailsAndPricing') as FormArray;
  }

  // Get Occupany pricing section
  getOccupancyPricingFormArray(pricingIndex: number): FormArray {
    return this.getRoomAndPricingFormArray().at(pricingIndex).get('occupancyPricings') as FormArray;
  }

  // Add the room Details and Pricing section 
  addRoomAndPrcingFormGroup() {
    this.getRoomAndPricingFormArray().push(this.createRoomAndPricingFormGroup());
  }
  // Add the pax form group
  addPaxPricingFormGroup(pricingIndex: number) {
    this.getOccupancyPricingFormArray(pricingIndex).push(this.createPaxFormgroup());
  }
  // Remove the roomDetails and Pricing section
  removeRoomAndPricingFormGroup(pricingIndex: number) {
    this.getRoomAndPricingFormArray().removeAt(pricingIndex);
  }
  // remove pax form Group
  removeOccupancyPricingFormGroup(pricingIndex: number, occupancyIndex: number) {
    this.getOccupancyPricingFormArray(pricingIndex).removeAt(occupancyIndex);
  }
  // clear all paxFormGroup
  clearAllOccupancies(pricingIndex: number) {
    this.getOccupancyPricingFormArray(pricingIndex).clear();
    this.addOccupancyPricing(pricingIndex);
  }

  // Auto fill the minRange of the new entry
  addOccupancyPricing(packageIndex: number): void {
    const occupancyFormArray = this.getOccupancyPricingFormArray(packageIndex);
    const lastOccupancyPricingIndex = occupancyFormArray.length - 1;
    const lastOccupancyData = occupancyFormArray.at(lastOccupancyPricingIndex)?.getRawValue();
    const newOccupancyPricing = this.createPaxFormgroup();
    if (lastOccupancyData?.paxRange?.maxRange != null) {
      newOccupancyPricing.get('paxRange')?.patchValue({
        minRange: +lastOccupancyData.paxRange?.maxRange + 1,
      });
    }
    occupancyFormArray.push(newOccupancyPricing);
  }
  maxRangeChanged(event: any, pricingIndex: number, occupancyIndex: number): void {
    const value = +(event.target.value);
    const formArray = this.getOccupancyPricingFormArray(pricingIndex);
    const nextOccupancy = formArray.at(occupancyIndex + 1)
    if (nextOccupancy) {
      nextOccupancy.get('paxRange.minRange')?.patchValue(value + 1);
    }
  }
  // serialize the grouped address fileds 
  serializeData(formData: any) {

    const serializeFormData = {
      ...formData,
      hotelDetails: {
        ...formData.hotelDetails,
        country: formData.hotelDetails?.groupedAddress?.country?.name,
        state: formData.hotelDetails?.groupedAddress?.state,
        city: formData.hotelDetails?.groupedAddress?.city,
        zipCode: formData.hotelDetails?.groupedAddress?.zipCode
      }
    }

    return serializeFormData;
  }

  // Edit hotel details and fetch existing details 
  getHotelDetails() {
    if (this.editHotelId) {
      this._hotelsService.getSpecificHotel(this.editHotelId).subscribe({
        next: (response: any) => {
          // Combine hotelDetails with top-level fields like hotelId if needed.
          const hotelOverviewDetails = {
            ...response,
            hotelId: response.hotelId,
            id: response.id,
            organizationId: response.organizationId,
            notes: response.notes
          };
          this.patchHotelForm(hotelOverviewDetails);

        },
        error: (error: any) => {
          console.error("Error fetching hotel details:", error);
        }
      });
    } else {
      console.error("No hotelId found in route parameters.");
    }
  }

  // Form Submission (Save)
  onSubmit(isSaveAndNew: boolean = false) {
    // Ensure form validity before submission
    this.hotelPricingForm.markAllAsTouched();
    // this._globalService.scrollToFirstInvalidControl();
    if (this.hotelPricingForm.invalid) {
      this._globalService.customAlert("Form is invalid, please fill all required fields.");
      return
    }
    // Convert Form data before submission
    const formData = this.serializeData(this.hotelPricingForm.value);
    // Edit mode: update hotel details if hotelId exists
    this._hotelsService.AddInventoryHotel(formData).subscribe({
      next: async (data: any) => {
        if (this.editHotelId)
          this._notificationServices.showMessage("Hotel details updated successfully");
        else
          this._notificationServices.showMessage("Hotel details Added successfully");

        // Refresh the hotels list
        this._reloadService.triggerInvenotryHotelsReload();

        // Save & Add New
        if (isSaveAndNew) {
          this.hotelPricingForm.reset();
          this.hotelPricingForm.patchValue({
            hotelDetails: {
              groupedAddress: {
                city: null,
                state: null,
                country: null,
                zipCode: null
              }
            }
          });
          return
        }
        // close modal pop-up
        this.closeModal();
      },
      error: (err) => {
        console.error("Error updating hotel data:", err);
        this._notificationServices.showMessage("Error updating hotel details", true);
      }
    });
  }

  // Patch the form with data
  private patchHotelForm(response: any): void {
    // prevent the  patching with invalid data
    if (!response) return;
    this.hotelPricingForm.patchValue({
      hotelId: response.hotelId,
      id: response.id,
      notes: response.notes,
      status: response.status,
      currency: response.currency,
      organizationId: response.organizationId,
      hotelDetails: {
        location: response.hotelDetails?.location || "",
        hotelName: response.hotelDetails?.hotelName || "",
        hotelCategory: response.hotelDetails?.hotelCategory || "",
        hotelWebsite: response.hotelDetails?.hotelWebsite || "",
        availableNights: response.hotelDetails?.availableNights || 0,
        address1: response.hotelDetails?.address1 || "",
        address2: response.hotelDetails?.address2 || "",
        groupedAddress: {
          country: response.hotelDetails?.country || "",
          state: response.hotelDetails?.state || "",
          city: response.hotelDetails?.city || "",
          zipCode: response.hotelDetails?.zipCode || ""
        },
        firstName: response.hotelDetails?.firstName || "",
        lastName: response.hotelDetails?.lastName || "",
        phoneNumber: response.hotelDetails?.phoneNumber || null,
        contactEmail: response.hotelDetails?.contactEmail || ""
      }
    });

    // Patch Room Details & Pricing if available
    if (Array.isArray(response.roomDetailsAndPricing)) {
      const roomArray = this.getRoomAndPricingFormArray();
      roomArray.clear(); // Clear existing rooms before patching

      // Loop Through Each Room & Patch Data
      response.roomDetailsAndPricing.forEach((room: any) => {
        roomArray.push(
          this._fb.group({
            roomType: [room.roomType || ""],
            nearByPlace: [room.nearByPlace || ""],
            mealPlans: [room.mealPlans || []],
            facilities: [room.facilities || []],

            // Patch Occupancy Pricing
            occupancyPricings: this._fb.array(
              (room.occupancyPricings || []).map((occupancy: any) =>
                this._fb.group({
                  paxRange: this._fb.group({
                    minRange: [occupancy.paxRange?.minRange || null],
                    maxRange: [occupancy.paxRange?.maxRange || null]
                  }),
                  occupancy: this._fb.group({
                    sgl: [occupancy.occupancy?.sgl || null],
                    dbl: [occupancy.occupancy?.dbl || null],
                    tpl: [occupancy.occupancy?.tpl || null]
                  }),
                  childCost: this._fb.group({
                    withBed: [occupancy.childCost?.withBed || ""],
                    withoutBed: [occupancy.childCost?.withoutBed || ""]
                  }),
                  priceValidity: this._fb.group({
                    startDate: [occupancy.priceValidity?.startDate || ""],
                    endDate: [occupancy.priceValidity?.endDate || ""]
                  })
                })
              )
            )
          })
        );
      });
    }
  }

  // close button
  closeModal() {
    this._modalController.dismiss();
  }

  formDataModified(): boolean {
    return (
      (this.hotelPricingForm.dirty || this.hotelPricingForm.invalid) && this.hotelPricingForm.touched
    );
  }

}
